"use client";

import React, { useId } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Icon } from "@/components/ui/Icon";
import { Wysiwyg } from "./Wysiwyg";

const accordionVariants = cva("w-full", {
  variants: {
    variant: {
      light: "",
      dark: "",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const accordionItemVariants = cva("group rounded-lg", {
  variants: {
    variant: {
      light: "bg-off-white text-black childLinkDark",
      dark: "bg-off-white text-black childLinkDark",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const triggerVariants = cva(
  "headline-5 px-lg py-md flex items-start justify-between gap-sm text-left list-none [&::-webkit-details-marker]:hidden transition-all duration-200 outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-0 rounded-lg group-open:rounded-b-none cursor-pointer",
  {
    variants: {
      variant: {
        light: "outline-purple-medium",
        dark: "outline-purple-medium",
      },
    },
    defaultVariants: {
      variant: "light",
    },
  }
);

const iconVariants = cva("", {
  variants: {
    variant: {
      light: "text-purple-medium mt-[3px]",
      dark: "text-purple-medium mt-[3px]",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const contentVariants = cva("px-lg py-md border-t-2", {
  variants: {
    variant: {
      light: "border-purple-medium",
      dark: "border-purple-medium",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface AccordionItemProps {
  title: string;
  text: string;
}

export interface AccordionProps extends VariantProps<typeof accordionVariants> {
  items: AccordionItemProps[];
  className?: string;
}

/**
 * Accordion built on native `<details>`/`<summary>`.
 *
 * The collapsed text stays in the server-rendered HTML so crawlers index it —
 * Radix's `Collapsible.Content` renders `isOpen && children`, so its content
 * was missing from the document until a visitor clicked. `name` groups the
 * items so only one is open at a time (ignored by browsers that lack it, which
 * then simply allow several open); the open/close animation lives in
 * `src/styles/index.css`.
 */
export const Accordion: React.FC<AccordionProps> = ({
  variant = "light",
  items,
  className = "",
}) => {
  const groupName = useId();

  return (
    <div className={accordionVariants({ variant, className })}>
      <div className="w-full flex flex-col gap-sm" data-slot="accordion">
        {items.map((item, index) => (
          <details
            key={index}
            data-slot="accordion-item"
            className={accordionItemVariants({ variant })}
            name={groupName}
          >
            <summary
              data-slot="accordion-trigger"
              className={triggerVariants({ variant })}
            >
              <h3 className="headline-5">{item.title}</h3>
              <span className={iconVariants({ variant })}>
                <Icon className="group-open:hidden" name="plus" size={20} />
                <Icon
                  className="hidden group-open:inline"
                  name="minus"
                  size={20}
                />
              </span>
            </summary>
            <div
              data-slot="accordion-content"
              className={contentVariants({ variant })}
            >
              <Wysiwyg html={item.text} mode="body" />
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};
